import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import successAnimation from "assets/lottie/Success.json"; // optionnel, voir note
import "./DiagnosticWizard.scss";
import IDTrackerPricing from "features/breaches/pages/IDTrackerPricing";
import EnhancedSpeedometer from "../Speedometer/EnhancedSpeedometer";
import iconSafe from "assets/lottie/Shield_icon.json";
import iconWarning from "assets/lottie/dangerALert2.json";
import { ASSISTANCE_PRICING, ID_TRACKER_PRICING, Pricing } from "shared/config/pricings";
import { Tooltip } from "react-tooltip";

/**
 * DiagnosticWizard
 * Props:
 *  - onComplete(result) optional callback called with { score, offer, details }
 */
const DiagnosticWizard = ({ onComplete }: any) => {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState<string | null>(null); // "victim" | "prevention"
  const [victimChoices, setVictimChoices] = useState(new Set());
  const [preventionChoices, setPreventionChoices] = useState(new Set());
  const [habitsChoices, setHabitsChoices] = useState(new Set());

  const [warning, setWarning] = useState("");

  const victimBancaireOptions = [
    { id: "banked_banq", label: "Fichage Banque de France" },
    { id: "account_thief", label: "Comptes frauduleux" },
    { id: "credit_insu", label: " crédits souscrits à votre insu" },
  ];

  const victimPublicTransportOptions = [
    { id: "transport_fines", label: "Vous recevez des amendes / PV pour des infractions que vous n’avez jamais commises" },
  ];

  const victimCompanyOptions = [
    { id: "company", label: "Usurpation d’identité d’entreprise, création de faux documents d'entreprise ou faux représentant légal d'une entreprise." },
  ];

  const victimDriverLicenceOptions = [
    { id: "driver_licence", label: "Véhicule acheté et/ou immatriculé à votre nom, fausses dénonciations, usurpation de votre plaque" },
  ];

  const victimRealEstateOptions = [
    { id: "real_estate", label: "Bail signé à votre insu, réponse à une fausse annonce immobilière, recouvrements de loyers" },
  ];


  const preventionRiskOptions = [
    { id: "fake_rental", label: "J’ai répondu à une fausse annonce de location" },
    { id: "investment_scam", label: "Arnaque d’investissement (crypto, etc.)" },
    { id: "lost_docs", label: "J’ai perdu/volé mes documents d’identité" },
    { id: "data_breach", label: "Je suis victime d’une fuite de données" },
  ];

  const preventionHabitOptions = [
    { id: "reuse_password", label: "J’utilise le même mot de passe sur plusieurs sites" },
    { id: "sent_unprotected_doc", label: "J’ai envoyé un document sans protection par email" },
    { id: "weak_security", label: "Je n’utilise pas de vérification en deux étapes." },
  ];

  const risques = [
    {
      ids: ["banked_banq", "account_thief", "credit_insu", "transport_fines", "company", "driver_licence", "real_estate"],
      label: "Saisie administratives sur salaire"
    },
    {
      ids: ["banked_banq", "account_thief", "credit_insu", "transport_fines", "company", "driver_licence", "real_estate"],
      label: "Fichage banque de France"
    },
    {
      ids: ["banked_banq", "account_thief", "credit_insu", "transport_fines", "company", "driver_licence", "real_estate"],
      label: "Toute autre usurpation dont vous n’auriez pas connaissance"
    },
    {
      ids: ["banked_banq", "account_thief", "credit_insu", "transport_fines", "company", "driver_licence", "real_estate"],
      label: "Diffusion de l’usurpation à d’autres secteur"
    },
    {
      ids: ["fake_rental"],
      label: "Perte d’argent"
    },
    {
      ids: ["fake_rental"],
      label: "Utilisation de tes données personnelles (détournement des comptes)"
    },
    {
      ids: ["fake_rental", "lost_docs"],
      label: "Usurpation d’identité"
    },
    {
      ids: ["fake_rental"],
      label: "Revente de tes informations sur le dark web"
    },
    {
      ids: ["investment_scam"],
      label: "Perte totale des fonds investis"
    },
    {
      ids: [
        "investment_scam"
      ],
      label: "Escroquerie à grande échelle (schéma Ponzi)"
    },
    {
      ids: [
        "investment_scam"
      ],
      label: "Vol d’identité ou hameçonnage"
    },
    {
      ids: [
        "investment_scam"
      ],
      label: "Infection par malware (via faux sites ou applis)"
    },
    {
      ids: ["lost_docs"],
      label: "Souscription frauduleuse à des crédits ou abonnements "
    },
    {
      ids: ["lost_docs"],
      label: "Création de comptes en ligne frauduleux"
    },
    {
      ids: ["lost_docs"],
      label: "Déclarations fiscales ou démarches administratives frauduleuses"
    },
    {
      ids: [
        "data_breach"
      ],
      label: "Compromission de comptes en ligne"
    },
    {
      ids: [
        "data_breach"
      ],
      label: "Tentatives de phishing ciblées"
    },
    {
      ids: [
        "data_breach"
      ],
      label: "Revente de données personnelles à des cybercriminels"
    },
    {
      ids: [
        "reuse_password"
      ],
      label: "Piratage massif de comptes par “credential stuffing” "
    },
    {
      ids: [
        "reuse_password"
      ],
      label: "Accès non autorisé à tes emails, banques, réseaux sociaux"
    },
    {
      ids: [
        "reuse_password"
      ],
      label: "Risque d’usurpation d’identité numérique"
    },
    {
      ids: ["sent_unprotected_doc"],
      label: "Interception du document par un tiers"
    },
    {
      ids: ["sent_unprotected_doc"],
      label: "Vol ou mauvaise utilisation des informations (RIB, identité, contrat…)"
    },
    {
      ids: ["sent_unprotected_doc"],
      label: "Accès non autorisé à des données sensibles en cas de piratage de la boîte mail"
    },
    {
      ids: ["sent_unprotected_doc"],
      label: "Souscription frauduleuse à des crédits ou abonnements"
    },
    {
      ids: ["weak_security"],
      label: "Compromission plus facile des comptes"
    },
    {
      ids: ["weak_security"],
      label: "Risque accru de piratage par phishing ou fuite de données"
    },
    {
      ids: ["weak_security"],
      label: "Accès non autorisé aux services (messagerie, banque, réseaux sociaux)"
    },
  ];



  // Toggle helpers
  const toggleSet = (setFn: any, setState: any, id: any) => {
    const next = new Set(setState);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setFn(next);
  };

  const getOffer = (score: number): Pricing => {
    if (score <= 10) {
      return ID_TRACKER_PRICING[0];
    } else if (score <= 40) {
      return ASSISTANCE_PRICING[0];
    } else if (score <= 80) {
      return ASSISTANCE_PRICING[1];
    } else {
      return ASSISTANCE_PRICING[2];
    }
  }

  // Scoring logic (simple but explainable). Returns {score, offer, breakdown}
  const result = useMemo(() => {
    // If victim -> heavy score
    if (profile === "victim") {
      // weight each chosen victim option; base high
      const chosen = victimChoices.size;
      // base 85 + 3*chosen (clamped)
      const raw = Math.min(100, 0 + chosen * 100 + (chosen > 2 ? 2 : 0));
      const offer = getOffer(raw)
      return {
        score: raw,
        offer,
        details: { profile, chosenCount: chosen, choices: Array.from(victimChoices) },
      };
    }

    // Prevention: combine risk events + habits
    if (profile === "prevention") {
      // weights
      const riskWeight = 190; // each risk option contributes equally up to 50%
      const habitWeight = 50; // habits contribute up to 30%
      const riskCount = preventionChoices.size;
      const habitCount = habitsChoices.size;

      const riskMaxItems = preventionRiskOptions.length;
      const habitMaxItems = preventionHabitOptions.length;

      const riskScore = Math.round((riskCount / riskMaxItems) * riskWeight);
      const habitScore = Math.round((habitCount / habitMaxItems) * habitWeight);

      // baseline safe 0
      let raw = 0 + riskScore + habitScore;

      // clamp and map to offer
      if (raw >= 60) {
        // stronger risk -> standard-high
      }
      raw = Math.min(100, Math.max(0, raw));
      let offer = getOffer(raw)
      if (habitScore > 10 && riskScore === 0) {
        offer = ID_TRACKER_PRICING[1]
      }
      return {
        score: raw,
        offer,
        details: {
          profile,
          riskCount,
          habitCount,
          riskChoices: Array.from(preventionChoices),
          habitChoices: Array.from(habitsChoices),
        },
      };
    }

    return { score: 0, offer: null, details: {} };
  }, [profile, victimChoices, preventionChoices, habitsChoices]);

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const handleSubmit = () => {
    // final step -> call callback if provided
    setStep(4);
  };

  // Small animated components / motion variants
  const cardVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  return (
    <div className="diag-root">
      <div className="diag-card">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              className="diag-step"
              key="intro"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={cardVariants}
            >
              <div className="diag-hero">
                <div className="diag-hero-left">
                  <h2>Diagnostiquez votre risque d'usurpation</h2>
                  <p className="muted">
                    En quelques étapes, évaluez votre exposition au risque. Le chatbot vous
                    orientera vers l'offre adaptée (Standard ou Premium).
                  </p>
                  <div className="actions-row">
                    <button className="btn primary" onClick={next}>
                      Démarrer le diagnostic
                    </button>
                  </div>
                </div>
                <div className="diag-hero-right">
                  {/* Lottie animation (optional). If not present, show placeholder */}
                  <div className="lottie-wrap">
                    <Lottie
                      animationData={successAnimation}
                      loop
                      style={{ width: 220, height: 220 }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              className="diag-step"
              key="step1"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={cardVariants}
            >
              <h3>Pourquoi êtes-vous ici ?</h3>
              <p className="muted">Choisissez l'option qui correspond le mieux à votre situation.</p>
              <div className="choice-grid">
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  tabIndex={0}
                  className={`choice-card ${profile === "victim" ? "active" : ""}`}
                  onClick={() => setProfile("victim")}
                >
                  <div className="choice-card-icon">
                    <Lottie
                      animationData={iconWarning}
                      loop
                      style={{ width: 70, height: 70 }}
                    />
                  </div>
                  <div className="choice-card-content">
                    <h4>Je suis victime d’usurpation</h4>
                    <p>J’ai reçu des amendes, ou quelqu’un utilise mon identité.</p>
                  </div>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className={`choice-card ${profile === "prevention" ? "active" : ""}`}
                  onClick={() => setProfile("prevention")}
                >
                  <div className="choice-card-icon">
                    <Lottie
                      animationData={iconSafe}
                      loop
                      style={{ width: 70, height: 70 }}
                    />
                  </div>
                  <div className="choice-card-content">
                    <h4>Je souhaite prévenir un risque d'usurpation</h4>
                    <p>Je n’ai pas subi d’usurpation mais je veux m'en protéger.</p>
                  </div>
                </motion.div>
              </div>

              <div className="actions-row">
                <button className="btn ghost" onClick={back}>
                  Retour
                </button>
                <button
                  className="btn primary"
                  onClick={() => {
                    if (!profile) {
                      setWarning("Veuillez choisir une option avant de continuer.");
                      setTimeout(() => setWarning(""), 2500); // auto-hide after 2.5s
                      return;
                    }
                    setStep(2);
                  }}
                >
                  Suivant
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && profile === "victim" && (
            <motion.div
              className="diag-step"
              key="victim"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={cardVariants}
            >
              <h3>Quel type d'usurpation rencontrez-vous ?</h3>
              <p className="muted">Cochez toutes les situations qui s'appliquent.</p>
              <div className="section">
                <h4>bancaire</h4>
                <div className="checkbox-grid">
                  {victimBancaireOptions.map((o) => (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      key={o.id}
                      className="checkbox-wrapper"
                    >
                      <input
                        type="checkbox"
                        id={o.id}
                        checked={victimChoices.has(o.id)}
                        onChange={() => toggleSet(setVictimChoices, victimChoices, o.id)}
                      />
                      <label htmlFor={o.id}>{o.label}</label>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="section">
                <h4>Transports public</h4>
                <div className="checkbox-grid">
                  {victimPublicTransportOptions.map((o) => (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      key={o.id}
                      className="checkbox-wrapper"
                    >
                      <input
                        type="checkbox"
                        id={o.id}
                        checked={victimChoices.has(o.id)}
                        onChange={() => toggleSet(setVictimChoices, victimChoices, o.id)}
                      />
                      <label htmlFor={o.id}>{o.label}</label>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="section">
                <h4>Entreprise</h4>
                <div className="checkbox-grid">
                  {victimCompanyOptions.map((o) => (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      key={o.id}
                      className="checkbox-wrapper"
                    >
                      <input
                        type="checkbox"
                        id={o.id}
                        checked={victimChoices.has(o.id)}
                        onChange={() => toggleSet(setVictimChoices, victimChoices, o.id)}
                      />
                      <label htmlFor={o.id}>{o.label}</label>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="section">
                <h4>Permis de conduire</h4>
                <div className="checkbox-grid">
                  {victimDriverLicenceOptions.map((o) => (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      key={o.id}
                      className="checkbox-wrapper"
                    >
                      <input
                        type="checkbox"
                        id={o.id}
                        checked={victimChoices.has(o.id)}
                        onChange={() => toggleSet(setVictimChoices, victimChoices, o.id)}
                      />
                      <label htmlFor={o.id}>{o.label}</label>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="section">
                <h4>Immobilier</h4>
                <div className="checkbox-grid">
                  {victimRealEstateOptions.map((o) => (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      key={o.id}
                      className="checkbox-wrapper"
                    >
                      <input
                        type="checkbox"
                        id={o.id}
                        checked={victimChoices.has(o.id)}
                        onChange={() => toggleSet(setVictimChoices, victimChoices, o.id)}
                      />
                      <label htmlFor={o.id}>{o.label}</label>
                    </motion.div>
                  ))}
                </div>
              </div>


              <div className="actions-row">
                <button className="btn ghost" onClick={() => setStep(1)}>
                  Retour
                </button>
                <button className="btn primary" onClick={ ()=> {
                  setStep(3);
                }}>
                  suivant
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && profile === "prevention" && (
            <motion.div
              className="diag-step"
              key="prevention"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={cardVariants}
            >
              <h3>Avez-vous subi un incident ?</h3>
              <p className="muted">Choisissez les incidents qui s'appliquent (si aucun, passez au suivant).</p>

              <div className="section">
                <h4>Incidents récents</h4>
                <div className="checkbox-grid">
                  {preventionRiskOptions.map((o) => (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      key={o.id}
                      className="checkbox-wrapper"
                    >
                      <input
                        type="checkbox"
                        id={o.id}
                        checked={preventionChoices.has(o.id)}
                        onChange={() => toggleSet(setPreventionChoices, preventionChoices, o.id)}
                      />
                      <label htmlFor={o.id}>{o.label}</label>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="section">
                <h4>Habitudes & pratiques</h4>
                <div className="checkbox-grid">
                  {preventionHabitOptions.map((o) => (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      key={o.id}
                      className="checkbox-wrapper"
                    >
                      <input
                        type="checkbox"
                        id={o.id}
                        checked={habitsChoices.has(o.id)}
                        onChange={() => toggleSet(setHabitsChoices, habitsChoices, o.id)}
                      />
                      <label htmlFor={o.id}>{o.label}</label>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="actions-row">
                <button className="btn ghost" onClick={() => setStep(1)}>
                  Retour
                </button>
                <button
                  className="btn primary"
                  onClick={() => {
                    // require at least one choice or proceed
                    setStep(3);
                  }}
                >
                  Suivant
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              className="diag-step"
              key="risks"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={cardVariants}
            >
              <h3>Risques identifiés</h3>
              <p className="muted">Voici les risques auxquels vous pourriez être exposé :</p>

              <div className="risks-container">
                {risques
                  .filter(risk => {
                    // Filter risks based on selected options
                    return risk.ids.some(id =>
                      preventionChoices.has(id) || habitsChoices.has(id) || victimChoices.has(id)
                    );
                  })
                  .map((risk, index) => (
                    <motion.div
                      key={index}
                      className="risk-item"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="risk-icon text-danger">⚠</div>
                      <div className="risk-content">
                        <p>{risk.label}</p>
                      </div>
                    </motion.div>
                  ))}
              </div>

              <div className="actions-row">
                <button className="btn ghost" onClick={() => setStep(2)}>
                  Retour
                </button>
                <button className="btn primary" onClick={handleSubmit}>
                  Voir mon offre
                </button>
              </div>
            </motion.div>
          )}

          {(step === 4) && (
            <motion.div
              className="diag-step final"
              key="final"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="final-wrap showOnlyOffers">

                <div className="final-left">

                  <div className="offer-block">
                    <h3>{result.offer?.head}</h3>
                    <div className="offer-name">{result.offer?.title}</div>
                  </div>
                  <div>
                  <IDTrackerPricing
                    onClick={() => {
                      if (onComplete) onComplete(result);
                    }}
                    isPage={false}
                    showOnlyOffers={true}
                    hideSwitch={false}
                    specificOffer={ result.offer ? [result?.offer] : []}
                    offerLink={result.offer?.link}
                  />
                  </div>

                  <div className="actions-row">
                    <button className="btn primary" onClick={() => {
                      if (onComplete) {
                        onComplete(result);
                      }
                      window.open("https://idprotect.fr/contact/", "_blank", "noopener,noreferrer");
                    }}>
                      Nous contacter
                    </button>
                    <button className="btn ghost" onClick={() => {
                      // reset
                      setProfile(null);
                      setVictimChoices(new Set());
                      setPreventionChoices(new Set());
                      setHabitsChoices(new Set());
                      setStep(0);
                    }}>
                      Recommencer
                    </button>
                  </div>
                </div>

                {/*<div className="final-right">
                  <Lottie animationData={successAnimation} style={{ width: 260 }} />
                </div>*/}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence mode="wait" >
          {step !== 0 && step !== 1 && (
            <div className={"d-flex flex-column center result-spidometer"}>
              <motion.div
                className={"d-flex flex-column"}
                key={`speedometer-${step}-${profile}`}
                initial={{ opacity: 0, scale: 0.6, y: 20 }}
                animate={{ opacity: 1, scale: 0.7, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                style={{ display: "flex", justifyContent: "center", marginBottom: "-43px" }}
              >
                <EnhancedSpeedometer score={result.score} />
              </motion.div>
              <DisplayRisque
                victimChoices={victimChoices}
                preventionChoices={preventionChoices}
                habitsChoices={habitsChoices}
                setStep={setStep}
                risques={risques}
              />
            </div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {warning && (
            <motion.div
              key="warn"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="warning-toast"
            >
              {warning}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};


const DisplayRisque = ({victimChoices, preventionChoices, habitsChoices, setStep, risques}: any) => {
  return (
    <>
      {/* Add risk count indicator */}
      {((preventionChoices?.size + habitsChoices?.size > 0) || (victimChoices?.size > 0)) && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="risk-count-badge"
          onClick={() => {
            // require at least one choice or proceed
            setStep(3);
          }}
        >
                   <span data-tooltip-id="my-tooltip"
                         data-tooltip-place="right-end"
                         data-tooltip-content="découvrir vos risques"
                         className={`tag-icon tag-error ml-2`}>
                     <span>⚠</span> {risques.filter((risk: { ids: any[]; }) =>
                     risk.ids.some(id => preventionChoices.has(id) || habitsChoices.has(id) || victimChoices.has(id)),
                   ).length} risques identifiés
                                                        </span>
          <Tooltip id="my-tooltip" variant="info" float={true} className="text-lowercase font-SM" />

        </motion.div>
      )}
    </>
  )
}

export default DiagnosticWizard;
